import { dom } from "@fortawesome/fontawesome-svg-core"
import Chart from "chart.js"
import "chartjs-plugin-datalabels"
import moment from "moment"
import { Subscriber, of, throwError, timer } from "rxjs"
import { fromFetch } from "rxjs/fetch"
import {
    filter,
    flatMap,
    map,
    switchMap,
    tap,
    withLatestFrom
} from "rxjs/operators"
import { webSocket } from "rxjs/webSocket"
import { liveDataUpdate } from "./app/live-data-update"
import { renderComplete } from "./app/render-complete"
import { wattageUpdate } from "./app/wattage-update"
import { getBatteryChartConfiguration } from "./chart-battery-configuration"
import { getChartConfiguration } from "./chart-configuration"
import { getHumidityChartConfiguration } from "./chart-humidity-configuration"
import { getSelfConsumptionChartConfiguration } from "./chart-self-consumption-configuration"
import { getStandbyUsageChartConfiguration } from "./chart-standby-usage-configuration"
import { getTemperatureChartConfiguration } from "./chart-temperature-configuration"
import { getVoltageChartConfiguration } from "./chart-voltage-configuration"
import { colors } from "./colors"
import { getViewFromDate, views } from "./helpers/views"
import { getVoltageLimit } from "./helpers/voltage"
import { transformLiveDataPoints } from "./live-data"
import "./styles/main.css"

require("./debug");

dom.watch()

/**
 * Chart.js global configuration
 * @url https://www.chartjs.org/docs/latest/configuration/?h=global
 */
Chart.defaults.global.defaultFontColor = colors.default_font
Chart.defaults.global.defaultFontFamily = "'Lato','Helvetica Neue', 'Helvetica', 'Arial', sans-serif"
Chart.defaults.global.defaultFontSize = 11

let chart, chartConfiguration, liveStream$, retryLiveStreamTimerId

// Subscriber for static (non live) graphs. Used to cancel any running http
// request when a new render call starts before the current request and graph
// rendering process is completed
let staticGraphStream$ = null

/**
 * Complete liveStream$ (includes websocket connection), if present
 * Remove old chart if exists
 */
const destroyChart = () => {
    if (typeof (chart) === 'object') {
        chart.destroy()
    }
}

const updateChart = (chartOptions, destroy = false) => {
    if (destroy) {
        destroyChart()
    }

    try {
        if (chart?.ctx == null) {
            // chart does not exists, create one
            chart = new Chart(document.getElementById('chart').getContext("2d"), chartConfiguration)
        } else {
            // update existing chart
            chart.update({
                ...chartOptions,
                // lazy (boolean): If true, the animation can be interrupted by other animations
                // easing (string): The animation easing function. See Animation Easing for possible values.
            })
        }
    } catch (err) {
        console.error('Could not render graph', err)
        renderComplete(false)
    }
}

export { destroyChart as clear }

/**
 * Render graph for a device
 *
 * @param {Object} options
 * @param {string} options.type                 "main_connection","solar","heatpump","electric_car","electric_bike","home_battery","gas","home_usage","other"
 *                                              Future: "always_on", "solar_total"
 * @param {string|Array} options.devices        One or more device identifiers for graph rendering.
 *                                              E.g. "p1dongle/lala" or ["p1dongle/lala","energymeter/lala2"]
 * @param {string} options.token                User token. Token (user) must have access to the device specified in "options.device"
 * @param {string} options.date                 The date in the following format:
 *                                              "2020/week/1", "2019/12/28/22", "2019/12/28", "2019/12" or "2019"
 * @param {string?} options.lang                The language iso. E.g. "en"(default) or "nl"
 * @param {string?} options.timezone            Timezone other than "Europe/Amsterdam" (default)
 * @param {boolean?} options.fullscreen         is renderer in fullscreen mode?
 * @param {Array} options.flags                 Additional flags array: ["three_phases"]
 */
export function render(options) {
    console.info(`render(${JSON.stringify(options)})`)

    if (staticGraphStream$ instanceof Subscriber) {
        staticGraphStream$.unsubscribe()
    }

    if (liveStream$ instanceof Subscriber) {
        console.debug('complete the live stream')
        liveStream$.complete()
    }

    // Clear any potential previous timeOut timer
    clearTimeout(retryLiveStreamTimerId)

    let {token, date, timezone, fullscreen, lang, devices, type, environment, flags = [], startMonth} = options

    if (lang == 'nb-NO') {
        lang = 'nb'
    }

    let threePhases = flags.includes("three_phases")

    // convert single device string to array
    if (typeof (devices) === 'string') {
        devices = [devices]
    }

    let view = getViewFromDate(date)

    console.debug(`view: ${view}`)
    console.debug(`type: ${type}`)
    console.debug(`devices: ${devices}`)
    console.debug(`threePhases: ${threePhases}`)
    console.debug(`startMonth: ${startMonth}`)

    if (type === 'standby_usage') {
        chartConfiguration = getStandbyUsageChartConfiguration(date, view, lang, type, fullscreen, false, environment, timezone)
    } else if (type === 'self_consumption') {
        chartConfiguration = getSelfConsumptionChartConfiguration(date, view, lang, type, fullscreen, false, environment, timezone)
    } else if (type === 'temperature') {
        chartConfiguration = getTemperatureChartConfiguration(date, view, lang, type, fullscreen, false, environment, timezone)
    } else if (type === 'humidity') {
        chartConfiguration = getHumidityChartConfiguration(date, view, lang, type, fullscreen, false, environment, timezone)
    } else if (type === 'overvoltage') {
        chartConfiguration = getVoltageChartConfiguration(date, view, lang, type, fullscreen, false, environment, timezone)
    } else if (type === 'battery') {
        let showBatteryPercentage = !flags.includes("hide_battery_percentage")
        chartConfiguration = getBatteryChartConfiguration(date, view, lang, type, fullscreen, false, environment, timezone, showBatteryPercentage)
    } else {
        chartConfiguration = getChartConfiguration(date, view, lang, type, fullscreen, false, environment, threePhases, timezone)
    }

    if (document.getElementById('chartjs-tooltip')) {
        document.getElementById('chartjs-tooltip').remove()
    }

    lang = lang || DEFAULT_LANG

    // set locale to display graph labels in correct language
    moment.locale(lang)

    if (views[view] === views.now) {
        renderLiveGraph(token, type, devices, options, timezone, threePhases)
    } else {
        renderStaticGraph(token, view, type, date, devices, threePhases, fullscreen, timezone, startMonth)
    }
}

const renderLiveGraph = (token, type, devices, renderOptions, timezone, threePhases = false) => {
    destroyChart()
    const liveWebsocket$ = webSocket(`${TSDB_READER_WS}/devices/date/now`)

    const intervalPeriod = 1000 // 1 second
    const nowPeriod = 60000     // 60 seconds

    // subscribe to websocket
    liveWebsocket$.next({token, type, devices, three_phases: threePhases})

    liveStream$ = timer(200, intervalPeriod).pipe( // a fixed timer determines the stream tick period
            withLatestFrom(liveWebsocket$.pipe( // on each tick use latest known data from websocket
                map(data => {
                    if (type === 'overvoltage') {

                        const dataPoints = [{
                            y: getVoltageLimit(timezone),
                            live: true
                        }]

                        return dataPoints.concat(Object.values(data.voltages).map(voltage => ({
                            y: voltage,
                            live: true
                        })))
                    } else if (type === 'battery') {
                        return [
                            {
                                y: data.wattage,
                                live: true,
                            },
                            {
                                y: data.charge_pct,
                                live: true,
                            }
                        ];
                    } else {
                        if (data.wattages?.l3 !== undefined) {
                            return Object.values(data.wattages).map(wattage => ({
                                y: wattage,
                                live: true
                            }))
                        } else {
                            return [{
                                y: data.wattage,
                                live: true
                            }]
                    }
                }
            })
        )),
        map(streams => streams[1]),

            // Add (local) current time to on each tick
            map(dataPoints => dataPoints.map(dataPoint => ({...dataPoint, t: new Date()})))
        ).subscribe(dataPoints => {
        if (chartConfiguration.data.datasets[0].data.length > 1.5 * nowPeriod / intervalPeriod) {
            // clean up old datapoints that are not visible in the graph anymore to spare memory
            chartConfiguration.data.datasets.forEach(set => {
                set.data.shift()
            })
        }

        // Set the minimal x-axis value relative to now on each (interval) re-draw
        chartConfiguration.options.scales.xAxes[0].ticks.min =
            moment(dataPoints[0].t).add(-1 * nowPeriod, 'milliseconds').toDate()

        dataPoints.forEach((dataPoint, index) => {
            chartConfiguration.data.datasets[index].data.push(dataPoint)
        })

        const dataset = transformLiveDataPoints(dataPoints, type)
        liveDataUpdate(dataset)

        // Backwards compatibility
        if (dataPoints[0].live && type !== 'overvoltage') {
            // Round "temperature" to 1 decimal, all other types to 0 decimals
            const roundFactor = ['temperature'].includes(type) ? 10 : 1

            // Send live wattage update to apps (Android/Ios)
            wattageUpdate(dataPoints.reduce((total, dataPoint) => total + dataPoint.y, 0), roundFactor)
        }

        updateChart({
            ...(dataPoints[0].live && {
                duration: intervalPeriod,
                easing: 'linear',
                lazy: true
            }),
            ...(!dataPoints[0].live && {duration: 0}),
        }, false)

    }, (err) => {
        console.error(err)

        /**
         * In case of any error we try to restart the live graph rendering repeatedly with a timeout of 1500 ms
         * In this way we might be able to "revive" the live graph in case of a websocket closure or whatsoever
         */
        retryLiveStreamTimerId = setTimeout(() => {
            render(renderOptions)
        }, 2500)

    }, () => {
        console.debug('onComplete()')
    })
}

const renderStaticGraph = (token, view, type, date, devices, threePhases, fullscreen, timezone, startMonth) => {
    const isSolarGraph = type === 'solar' || type === 'solar_combined'
    const supportsWattage = ['gas', 'temperature', 'humidity', 'overvoltage'].includes(type) === false
    const dataFetch$ = fromFetch(`${TSDB_READER_URL}/devices/date/${date === 'now' ? 'recent' : date}`, {
        method: 'POST',
        headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
            devices,
            type,
            values: true,
            wattage: supportsWattage && views[view] <= views.day,
            ...(type === 'gas' && views[view] <= views.day && {gb: '5m'}), // groupBy 5m for gas
            ...(type !== 'gas' && views[view] === views.day && {gb: fullscreen ? '5m' : '15m'}), // groupBy 5m or 15m
            ...(type !== 'gas' && views[view] < views.day && {gb: '1m'}), // groupBy 1m for power for views < day
            ...(type !== 'gas' && views[view] === views.now && {gb: '1s'}), // groupBy 1s for power for view = now
            ...(timezone && {tz: timezone}),
            fill: null,
            ...(views[view] <= views.day && {fill: 'linear'}),
            ...(views[view] === views.now && {fill: 'previous'}),
            three_phases: threePhases,
            start_month: startMonth,
        })
    }).pipe(
        switchMap(response => response.json()),
        flatMap(res => res.error ? throwError(new Error(JSON.stringify(res))) : of(res)),
        filter(res => res.values),
        tap(res => {
            console.groupCollapsed(`Recent values (${res.values.length})`);
            console.table(res);
            console.groupEnd()
        }),
        flatMap(res => res.values),
        map(energyPoint => {
            let dataPoint = []

            // Render three phases graph
            // Check if we actually have a third phase wattage (active Energy+ subscriptions required, backend will validate)
            if (energyPoint.wattages?.l3 !== undefined) {
                Object.values(energyPoint.wattages).forEach(wattage => {
                    dataPoint.push({
                        t: new Date(energyPoint.time),
                        y: wattage
                    })
                })
            } else {
                switch (type) {
                    case 'standby_usage':
                        if (views[view] <= views.day) {
                            // day graph
                            dataPoint = [{
                                t: new Date(energyPoint.time),
                                y: energyPoint.wattage
                            }]
                        } else {
                            // week, month, year graph

                            // make a diff bar of import - standby, needed for the stack graphs
                            // should never become negative (possible when "home_usage data" is incomplete, because of bad wifi etc.)
                            if (energyPoint.standby_usage !== null) {
                                dataPoint = [{
                                    t: new Date(energyPoint.time),
                                    y: Math.max(0, energyPoint.import - energyPoint.standby_usage)
                                }]
                            } else {
                                dataPoint = [{
                                    t: new Date(energyPoint.time),
                                    y: null
                                }]
                            }
                        }

                        dataPoint.push({
                            t: new Date(energyPoint.time),
                            y: energyPoint.standby_usage
                        })
                        break

                    case 'water':
                        dataPoint = [{
                            t: new Date(energyPoint.time),
                            y: energyPoint.water !== null ? Math.max(0, energyPoint.water) : null
                        }]
                        break

                    case 'gas':
                        dataPoint = [{
                            t: new Date(energyPoint.time),
                            y:  energyPoint.gas !== null ? Math.max(0, energyPoint.gas) : null
                        }]
                        break

                    case 'self_consumption':
                        if (views[view] <= views.day) {
                            // day graph
                            if (energyPoint.import === null || energyPoint.usage === null || energyPoint.export === null) {
                                return  [
                                    // home usage
                                    {
                                        t: new Date(energyPoint.time),
                                        y: null
                                    },

                                    // solar combined
                                    {
                                        t: new Date(energyPoint.time),
                                        y: null,
                                    },

                                    // self consumption
                                    {
                                        t: new Date(energyPoint.time),
                                        y: null,
                                    },
                                ]
                            }

                            dataPoint = [
                                // home usage
                                {
                                    t: new Date(energyPoint.time),
                                    y: energyPoint.import
                                },

                                // solar combined
                                {
                                    t: new Date(energyPoint.time),
                                    y: energyPoint.export < 0 ? -energyPoint.export : 0
                                },

                                // self consumption
                                {
                                    t: new Date(energyPoint.time),
                                    y: energyPoint.usage < 0 ? -energyPoint.usage : 0
                                },
                            ]
                        } else {
                            // week, month, year graph
                            dataPoint = [
                                {
                                    t: new Date(energyPoint.time),
                                    y: energyPoint.import
                                },
                                {
                                    t: new Date(energyPoint.time),
                                    y: energyPoint.export
                                },
                                {
                                    t: new Date(energyPoint.time),
                                    y: energyPoint.usage
                                }
                            ]
                        }
                        break

                    case 'temperature':
                    case 'humidity':
                        dataPoint = [
                            {
                                t: new Date(energyPoint.time),
                                y: energyPoint.measurement
                            },
                            {
                                t: new Date(energyPoint.time),
                                y: energyPoint.minMeasurement
                            },
                            {
                                t: new Date(energyPoint.time),
                                y: energyPoint.maxMeasurement
                            }
                        ]
                        break

                    case 'overvoltage':

                        // We pushing null values here to ensure the rendering code has the assurance
                        // that phases are always in certain positions.
                        dataPoint = [
                            {
                                t: new Date(energyPoint.time),
                                y: getVoltageLimit(timezone)
                            },
                            {
                                t: new Date(energyPoint.time),
                                y: energyPoint.l1 ?? null
                            },
                            {
                                t: new Date(energyPoint.time),
                                y: energyPoint.l2 ?? null
                            },
                            {
                                t: new Date(energyPoint.time),
                                y: energyPoint.l3 ?? null
                            }
                        ]

                        break

                    case 'battery':

                        if (views[view] <= views.day) {
                        dataPoint = [
                            {
                                t: new Date(energyPoint.time),
                                y: energyPoint.usage
                            },
                            {
                                t: new Date(energyPoint.time),
                                y: energyPoint.percentage
                            }
                        ]
                        } else {
                            // week, month, year graph
                            dataPoint = [
                                {
                                    t: new Date(energyPoint.time),
                                    y: energyPoint.import !== null ? Math.max(0, energyPoint.import) : null
                                },
                                {
                                    t: new Date(energyPoint.time),
                                    y: energyPoint.export !== null ? Math.max(0, energyPoint.export) : null
                                }
                            ]
                        }

                        break

                    default:
                        if (views[view] <= views.day) {
                            // day graph
                            dataPoint = [{
                                t: new Date(energyPoint.time),
                                y: energyPoint.wattage
                            }]
                        } else {
                            // week, month, year graph
                            dataPoint = [
                                {
                                    t: new Date(energyPoint.time),
                                    y: energyPoint.import
                                },
                                {
                                    t: new Date(energyPoint.time),
                                    y: energyPoint.export
                                }
                            ]
                        }
                }
            }

            return dataPoint
        }),
    )

    staticGraphStream$ = dataFetch$.subscribe(dataPoints => {
        /**
         * dataPoints[0] = "gas", "wattage", "import", "first phase", "voltage_limit", "battery:power" or "battery:import"
         * dataPoints[1] = "export", "second phase", "voltage_first_phase", "standby", "battery:percentage" or "battery:export"
         * dataPoints[2] = "third phase" or "voltage_second_phase"
         * dataPoints[3] = "voltage_third_phase"
         */
        dataPoints.forEach((dataPoint, index) => {
            chartConfiguration.data.datasets[index].data.push(dataPoint)
        })
    }, (err) => {
        try {
            console.error(JSON.parse(err.message))
        } catch (error) {
            console.error(err)
        }
        renderComplete(false)
    }, () => {
        // Day temperature/humidity graphs: flatten "min" and "max" datasets to 1 value: the
        // "min" and "max" of the mean values
        if (
            (type === 'temperature' || type === 'humidity') && views[view] === views.day &&
            chartConfiguration.data.datasets[1]?.data.length &&
            chartConfiguration.data.datasets[2]?.data.length
        ) {
            const meanValues = chartConfiguration.data.datasets[0]?.data

            const minValue = meanValues.reduce((prev, curr) => prev < curr.y || curr.y === null ? prev : curr.y, Infinity)
            const maxValue = meanValues.reduce((prev, curr) => prev > curr.y || curr.y === null ? prev : curr.y, -Infinity)

            chartConfiguration.data.datasets[1].data = chartConfiguration.data.datasets[1]?.data.map((dataPoint) => ({
                ...dataPoint,
                y: dataPoint.y === null ? null : minValue
            }))

            chartConfiguration.data.datasets[2].data = chartConfiguration.data.datasets[2]?.data.map((dataPoint) => ({
                ...dataPoint,
                y: dataPoint.y === null ? null : maxValue
            }))
        }

        /**
         * Horizontal alignment of datalabels should be "top" when we have 1 visible dataset instead of 2
         * Hide datasets with no data when no data is expected
         */
        if (views[view] >= views.week) {
            if (!isSolarGraph && type !== 'standby_usage' && type !== 'temperature') {
                for(let i = 1; i < chartConfiguration.data.datasets.length; i++) {
                    if (!chartConfiguration.data.datasets[i]?.data.some((dataPoint) => dataPoint.y > 0)) {
                        if (i === 1) {
                            // When we do not have any data in datasets[1] we align the datalabels for dataset 0 to "top"
                            chartConfiguration.data.datasets[0].datalabels.align = 'top'
                        }

                        // Hide dataset when all datapoints in dataset are zero
                        chartConfiguration.data.datasets[i].hidden = true
                    }
                }

            }

            if (isSolarGraph) {
                chartConfiguration.data.datasets[1].datalabels.align = 'top'
                chartConfiguration.data.datasets.forEach((dataset, index) => {
                    if (index !== 1) {
                        chartConfiguration.data.datasets[index].hidden = true // hide all other datasets for solar graphs
                    }
                })
            }

            if (type === 'standby_usage') {
                chartConfiguration.data.datasets[1].datalabels.align = 'top'
                chartConfiguration.data.datasets[0].datalabels.display = false // hide datalabel on import bar
            }
        }

        updateChart({}, true)
    })
}